import * as React from 'react';
import  { useRef, useEffect } from 'react';
import {DOMNode, domToReact, htmlToDOM} from 'html-react-parser';
import { Popover } from '@blueprintjs/core';
import classNames from 'classnames';

export interface IAccessibleDropProps {
    /* DOM to parse */
    dom: string | DOMNode[];
    /** Click event to bind to toggler */
    onClick?: (event) => void;
    /** Unsafe config */
    config?: any;
    /** Component mapper for parser subcomponents */
    componentMapper?: (maybeComponent: string, maybeContent: string, maybeConfig) => React.Component | React.ReactNode;
}

/**
 * Renders DOM string to clickable link.
 */
const AccessibleDrop: React.FunctionComponent<IAccessibleDropProps> = (props) => {
    const [open, setOpen] = React.useState(false);
    const [minWidth, setMinWidth] = React.useState(0);
    const ref = useRef<HTMLDivElement>(null);

    const handleClosePopover = () => {
        setOpen(false);
        if(props.config?.setOpen) { props.config.setOpen(false); }
    };

    useEffect(() => {
        // @ts-ignore
        setMinWidth(ref.current.offsetWidth);
    }, [ref.current]);

    const parserOptions = {
        replace: (domNode: any) => {
            if (domNode.name) {
                if ((domNode.name === 'a' || domNode.attribs.class === 'toggler') && !!domNode.next && !!domNode.next.children) {
                    const inner = domToReact(domNode.children, parserOptions);
                    const { class: className, ...rest } = domNode.attribs;
                    const Elem = domNode.name;

                    return (
                        <Popover
                            enforceFocus={true}
                            interactionKind={props.config?.interactionMode && props.config.interactionMode}
                            position={props.config?.position ? props.config.position : 'bottom-left'}
                            usePortal={false}
                            fill={props.config?.fill ? props.config.fill : false}
                            minimal={true}
                            autoFocus={true}
                            isOpen={open}
                            onOpening={(el) => {
                                if (props.config?.fill === true) { el.style.minWidth = minWidth + 'px'; }
                            }}
                            boundary={props.config?.boundary ? props.config.boundary : 'scrollParent'}
                            onClose={handleClosePopover}
                            modifiers={{
                                offset: {
                                    data: props.config?.offset ? props.config.offset : [0, 0],
                                },
                                flip: {
                                    enabled: false,
                                },
                            }}
                            portalClassName={classNames('site-menu-portal', props.config?.portalClassName && props.config.portalClassName)}
                            popoverClassName={classNames('site-menu-popover', props.config?.popoverClassName && props.config.popoverClassName)}
                            content={
                                domNode.next.name === 'ul' ? (
                                    <ul aria-live="polite" className={classNames('site-menu-items', domNode.next.attribs.class)}>
                                        {domToReact(domNode.next.children, parserSubOptions)}
                                    </ul>
                                ) : (
                                    <div aria-live="polite">
                                        {domToReact(domNode.next.children, parserSubOptions)}
                                    </div>
                                )
                            }

                            transitionDuration={0}
                            hoverOpenDelay={0}
                            hoverCloseDelay={100}
                        >
                            <Elem
                                {...rest}
                                className={className}
                                suppressHydrationWarning={true}
                                role="button"
                                ref={ref}
                                aria-haspopup="true"
                                onClick={(e) => {
                                    if(props.config?.interactionMode !== 'hover') {
                                        e.preventDefault();
                                        if(props.config?.setOpen) {
                                            props.config?.setOpen(true);
                                        }
                                        setOpen(true);
                                    }
                                }}
                                onMouseEnter={(e) => {
                                    e.preventDefault();
                                    if(props.config?.interactionMode === 'hover') {
                                        if(props.config?.setOpen) {
                                            props.config?.setOpen(true);
                                        }
                                        setOpen(true);
                                    }
                                }}
                            >
                                {props.config?.arrow && (<span className="toggle-icon"><img src={props.config.arrow} alt=""/></span>)}
                                {inner}
                            </Elem>
                        </Popover>
                    );
                }
                if (domNode.name === 'a' && (!domNode.next || !domNode.next.children)) {
                    const { class: className, ...rest } = domNode.attribs;
                    return (
                        <a
                            {...rest}
                            className={className}
                            onClick={props.onClick}
                            tabIndex={0}
                        >
                            {domToReact(domNode.children)}
                        </a>
                    );
                }
                if (domNode.name === 'ul' || domNode.name === 'div') {
                    return (
                        <React.Fragment/>
                    );
                }
            }
            return false;
        },
    };

    const parserSubOptions = {
        replace: (domNode: any) => {
            if (domNode.name) {
                if (domNode.attribs['data-subcomponent'] && props.componentMapper){
                    const maybeConfig = JSON.parse(domNode.attribs['data-config'] || '{}');
                    const maybeComponent = domNode.attribs['data-subcomponent'];
                    const maybeContent = domNode.children ?? '';
                    const { class: className, ...rest } = domNode.attribs;
                    const mappedComponent = props.componentMapper(maybeComponent, maybeContent, maybeConfig);

                    if (mappedComponent) {
                        return (
                            <domNode.name className={className} {...rest}>
                                {mappedComponent}
                            </domNode.name>
                        );
                    }
                }

                if (domNode.attribs['data-component'] && props.componentMapper){
                    const maybeConfig = JSON.parse(domNode.attribs['data-config'] || '{}');
                    const maybeComponent = domNode.attribs['data-component'];
                    const maybeContent = domNode.children ?? '';
                    const { class: className, ...rest } = domNode.attribs;
                    const mappedComponent = props.componentMapper(maybeComponent, maybeContent, maybeConfig);

                    if (mappedComponent) {
                        return (
                            <domNode.name className={className} {...rest}>
                                {mappedComponent}
                            </domNode.name>
                        );
                    }
                }

                if (domNode.name === 'li') {
                    const { class: className, ...rest } = domNode.attribs;
                    return (
                        <li
                            {...rest}
                            className={className}
                        >
                            {domToReact(domNode.children, parserSubOptions)}
                        </li>
                    );
                }

                if (domNode.name === 'a' && !!domNode.next) {
                    const { class: className, ...rest } = domNode.attribs;
                    return (
                        <React.Fragment>
                            {domToReact(domNode.children, parserOptions)}
                            <a
                                {...rest}
                                className={className}
                                {...{ tabIndex: open ? 0 : -1 }}
                                onClick={(e) => {
                                    if (!domNode.attribs.href) {
                                        e.preventDefault();
                                    }
                                    handleClosePopover();
                                }}
                            >
                                {domToReact(domNode.next.children, parserSubOptions)}
                            </a>

                        </React.Fragment>
                    );
                }

                if (domNode.name === 'a' && !domNode.next) {
                    const { class: className, ...rest } = domNode.attribs;
                    return (
                        <React.Fragment>
                            <a
                                {...rest}
                                className={className}
                                {...{ tabIndex: open ? 0 : -1 }}
                                onClick={(e) => {
                                    if (!domNode.attribs.href) {
                                        e.preventDefault();
                                    }
                                    handleClosePopover();
                                }}
                            >
                                {domToReact(domNode.children, parserOptions)}
                            </a>
                        </React.Fragment>
                    );
                }

                if (domNode.name === 'ul') {
                    const { class: className, ...rest } = domNode.attribs;
                    return (
                        <ul className={className} {...rest}>
                            {domToReact(domNode.children, parserSubOptions)}
                        </ul>
                    );
                }
            }
            return false;
        },
    };

    // Main output

    const domTree = Array.isArray(props.dom) ? props.dom : htmlToDOM(props.dom.replace(/>\s+</g, '><'));

    return (
        <React.Fragment>
            {domToReact(domTree, parserOptions)}
        </React.Fragment>
    );
};

AccessibleDrop.displayName = 'AccessibleDrop';

export default AccessibleDrop;